import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sun, Moon } from 'lucide-react';
import { useAuth } from '@/shared/hooks/useAuth';
import { type Palette } from '@/shared/context/ThemeContext';
import { useThemeContext } from '@/shared/context/useThemeContext';
import styles from './UserMenu.module.css';

const PALETTES: { id: Palette; label: string; swatchClass: string }[] = [
  { id: 'emerald', label: 'Emerald', swatchClass: styles.swatchEmerald },
  { id: 'sunset', label: 'Sunset', swatchClass: styles.swatchSunset },
];

export function UserMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const { theme, palette, toggleTheme, setPalette } = useThemeContext();
  const navigate = useNavigate();

  const handleLogout = async () => {
    logout();
    navigate('/login');
  };

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : (user?.email?.[0].toUpperCase() ?? '?');

  return (
    <div className={styles.wrapper}>
      <button className={styles.trigger} onClick={() => setIsOpen(!isOpen)}>
        <div className={styles.avatar}>{initials}</div>
        <div className={styles.userInfo}>
          <span className={styles.userName}>{user?.name || user?.email}</span>
        </div>
        <span className={styles.chevron}>▾</span>
      </button>

      {isOpen && (
        <>
          <div className={styles.backdrop} onClick={() => setIsOpen(false)} />
          <div className={styles.dropdown}>
            <div className={styles.userDetails}>
              <div className={styles.avatarLg}>{initials}</div>
              <div>
                <div className={styles.name}>{user?.name}</div>
                <div className={styles.email}>{user?.email}</div>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.paletteSection}>
              <div className={styles.paletteLabel}>Color theme</div>
              <div className={styles.paletteSwatches} role="group" aria-label="Color theme">
                {PALETTES.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`${styles.swatch} ${p.swatchClass} ${
                      palette === p.id ? styles.swatchActive : ''
                    }`}
                    onClick={() => setPalette(p.id)}
                    aria-pressed={palette === p.id}
                    aria-label={p.label}
                    title={p.label}
                  />
                ))}
              </div>
            </div>
            <div className={styles.divider} />
            <button
              className={styles.dropdownItem}
              onClick={() => {
                navigate('/settings');
                setIsOpen(false);
              }}
            >
              Settings
            </button>
            <button className={styles.dropdownItem} onClick={toggleTheme}>
              <span className={styles.itemLabel}>
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </span>
              {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <div className={styles.divider} />
            <button className={styles.dropdownItem} onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
